import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import PageHelmet from "../component/common/Helmet";
import HeaderAbs from "../component/header/HeaderAbs";
import FooterTwo from "../component/footer/FooterTwo";

const RecentWork = () => {
    const internalWork = useMemo(
        () => [
            {
                title: "ICE / REX — Internal Enterprise Agents",
                body: (
                    <>
                        <strong>ICE</strong> is Rackspace’s “Intelligent Co-worker for the
                        Enterprise,” a generative AI system designed for enterprise use with
                        curated enterprise data in a secure private tenant. Rackspace leadership
                        also refers to <strong>REX</strong> as an internal sales-focused agent
                        brand embedded in operations.
                    </>
                ),
                links: [
                    {
                        label: "What ICE is",
                        href: "https://www.rackspace.com/newsroom/rackspace-technology-launches-intelligent-co-worker-ice-enterprise",
                    },
                    {
                        label: "FAIR use cases",
                        href: "https://fair.rackspace.com/generative-ai-use-cases/",
                    },
                    {
                        label: "ICE in practice",
                        href: "https://www.rackspace.com/blog/foundry-for-ai-rackspace-first-six-months",
                    },
                ],
                demo: {
                    label: "Watch demo",
                    href: "https://www.youtube.com/watch?v=wzZAmqqq15c",
                },
            },
            {
                title: "RITA — IT Self‑Service Assistant",
                body: (
                    <>
                        <strong>RITA</strong> is an IT self‑service assistant built under FAIR
                        that answers IT service requests (e.g., password resets,
                        hardware/software requests, IT/security policy Q&amp;A)
                        and links responses back to specific source-document sections for
                        resolution.
                    </>
                ),
                links: [
                    {
                        label: "RITA overview",
                        href: "https://www.rackspace.com/newsroom/rackspace-technology-announces-fair-learn",
                    },
                    {
                        label: "FAIR roadshow (ICE + RITA demos)",
                        href: "https://fair.rackspace.com/insights/highlights-from-the-fair-generative-ai-roadshow/",
                    },
                    {
                        label: "FAIR methodology",
                        href: "https://www.rackspace.com/newsroom/rackspace-technology-signs-aws-multi-year-sca",
                    },
                ],
                demo: {
                    label: "Demo reference",
                    href: "https://ignite.microsoft.com/en-US/sessions/ODSP1511",
                },
            },
            {
                title: "Forward‑Deployed Engineering — Palantir Foundry/AIP & Sema4.ai",
                body: (
                    <>
                        As <strong>Principal Forward Deployed Engineer</strong> I work across the
                        full arc of a Foundry engagement: technical sales and honest platform fit,
                        initial architecture and solution engineering, then proof of value and
                        bootcamp alongside the customer's team and ours, and on through delivery.
                        My own build work concentrates on <strong>AI engineering with AIP</strong>{" "}
                        and agentic automation. On the platform side, Rackspace publicly partners
                        with <strong>Sema4.ai</strong> for production-ready agent deployment with
                        governance and security.
                    </>
                ),
                links: [
                    { label: "Case study", anchor: "#foundry-fde" },
                    {
                        label: "Rackspace + Sema4.ai",
                        href: "https://ir.rackspace.com/news-releases/news-release-details/rackspace-technology-and-sema4ai-launch-industrys-first-scalable",
                    },
                    { label: "Palantir Foundry", href: "https://www.palantir.com/platforms/foundry/" },
                    { label: "Palantir AIP", href: "https://www.palantir.com/platforms/aip/" },
                ],
            },
        ],
        []
    );

    const marketplaceWork = useMemo(
        () => [
            {
                title: "LegalMind — Contract Intelligence Accelerator",
                body: (
                    <>
                        <strong>Contract intelligence, governed by design.</strong>{" "}
                        Bedrock Document Converse extracts schema-validated contract fields with
                        anchors; Knowledge Bases back Q&amp;A with citations. Serverless
                        delivery with AppConfig routing and audit-friendly traces.
                        <br />
                        <span>
                            Architecture: S3 + CloudFront (OAC/WAF) SPA, API Gateway + Lambda
                            orchestrator, SSE streaming behind CloudFront (origin secret + strict
                            CORS), and DynamoDB/S3 persistence.
                        </span>
                    </>
                ),
                diagram: {
                    src: "/assets/images/proof/legalmind-architecture.png",
                    alt: "LegalMind architecture diagram",
                },
                links: [
                    {
                        label: "AWS Marketplace",
                        href: "https://aws.amazon.com/marketplace/pp/prodview-fcjw6afwpfg4c",
                    },
                    { label: "Diagram", href: "/assets/images/proof/legalmind-architecture.png" },
                    { label: "Runbook", to: "/runbooks/legalmind" },
                ],
            },
            {
                title: "Careflow — Ambient Clinical Documentation",
                body: (
                    <>
                        <strong>Realtime voice agent + follow‑up automation.</strong>{" "}
                        Bedrock Realtime (<strong>Nova Sonic</strong>) powers low‑latency
                        speech‑to‑speech encounters, paired with Step Functions/EventBridge
                        workflows and tenant-scoped portal rollout via AppConfig.
                        <br />
                        <span>
                            Realtime path: Web Audio → AudioWorklet → API Gateway WebSocket → Lambda
                            broker → Bedrock Nova Sonic, with PrivateLink for PHI traffic.
                        </span>
                    </>
                ),
                diagram: {
                    src: "/assets/images/proof/careflow-architecture.png",
                    alt: "Careflow architecture diagram",
                },
                links: [
                    {
                        label: "AWS Marketplace",
                        href: "https://aws.amazon.com/marketplace/pp/prodview-5udnvuvm235gi",
                    },
                    { label: "Diagram", href: "/assets/images/proof/careflow-architecture.png" },
                    { label: "Security posture", to: "/runbooks/careflow" },
                ],
            },
            {
                title: "HealthBridge — Agent Navigator",
                body: (
                    <>
                        <strong>Guardrail-first portal navigation.</strong> Powered by{" "}
                        <strong>Nova Act</strong> with page-scoped prompts + JSON Schemas,
                        dry‑run default behavior, and AppConfig‑gated mutations. Each step emits
                        canonical Status JSON persisted with TTL for audit-friendly review.
                    </>
                ),
                diagram: {
                    src: "/assets/images/proof/healthbridge-architecture.png",
                    alt: "HealthBridge architecture diagram",
                },
                links: [
                    {
                        label: "AWS Marketplace",
                        href: "https://aws.amazon.com/marketplace/pp/prodview-uf4aqot4w3fm2",
                    },
                    { label: "Diagram", href: "/assets/images/proof/healthbridge-architecture.png" },
                    { label: "Operator guide", to: "/runbooks/healthbridge" },
                ],
            },
        ],
        []
    );

    const structuredData = useMemo(
        () => ({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Recent Work",
            url: "https://www.theomefford.com/recent-work",
        }),
        []
    );

    const renderLinks = (links) => (
        <div className="mt--20 d-flex flex-wrap" style={{ gap: "10px" }}>
            {links.map((link) =>
                link.anchor ? (
                    // Same-page jump: a plain anchor so the browser handles scrolling.
                    <a key={link.label} className="rn-button-style--2 btn-solid" href={link.anchor}>
                        {link.label}
                    </a>
                ) : link.to ? (
                    <Link key={link.label} className="rn-button-style--2 btn-solid" to={link.to}>
                        {link.label}
                    </Link>
                ) : (
                    <a
                        key={link.label}
                        className="rn-button-style--2 rn-btn-dark"
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {link.label}
                    </a>
                )
            )}
        </div>
    );

    return (
        <React.Fragment>
            <PageHelmet
                pageTitle="Recent Work"
                description="Production-first enterprise AI: Palantir Foundry forward-deployed engineering, internal agents, and AWS Marketplace deployments—backed by diagrams, runbooks, and demos."
                canonical="https://www.theomefford.com/recent-work"
                structuredData={structuredData}
            />

            <HeaderAbs homeLink="/" logo="symbol-dark" color="color-black" />

            <div className="rn-page-title-area pt--120 pb--70 bg_color--1">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="blog-single-page-title text-center pt--60">
                                <h2 className="title theme-gradient">Recent Work</h2>
                                <p className="description">
                                    Production-first enterprise AI: Palantir Foundry forward-deployed engineering, internal agents, and AWS Marketplace deployments—backed by diagrams, runbooks, and demos.
                                </p>
                                <p className="description mt--20" style={{ maxWidth: 860, margin: "0 auto" }}>
                                    <strong>Proof policy:</strong> metrics and claims are published only when sourced publicly or explicitly approved.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Row 1: Internal / Platforms */}
            <div className="rn-blog-area pt--80 pb--80 bg_color--5">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section-title text-center">
                                <h2 className="title">Internal Platforms &amp; Engagements</h2>
                                <h4 className="title">Enterprise agents and forward-deployed delivery patterns.</h4>
                            </div>
                        </div>
                    </div>

                    <div className="row mt--40">
                        {internalWork.map((item) => (
                            <div key={item.title} className="col-lg-4 col-md-6 col-12 mt--30">
                                <div className="blog blog-style--1 rn-work-card">
                                    <div className="content">
                                        <h4 className="title">{item.title}</h4>
                                        <div className="blog-text">
                                            <p>{item.body}</p>
                                        </div>

                                        {renderLinks(item.links)}

                                        {item.demo?.href ? (
                                            <div className="mt--20">
                                                <a
                                                    className="rn-button-style--2 btn-solid"
                                                    href={item.demo.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {item.demo.label || "Watch demo"}
                                                </a>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Row 1b: Palantir Foundry forward-deployed engineering */}
            <div id="foundry-fde" className="rn-blog-area pt--80 pb--80 bg_color--1">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section-title text-center">
                                <h2 className="title">Palantir Foundry — Forward Deployed Engineering</h2>
                                <h4 className="title">Principal Forward Deployed Engineer, Rackspace Technology</h4>
                            </div>
                        </div>
                    </div>

                    <div className="row mt--40">
                        <div className="col-lg-10 offset-lg-1">
                            <p className="description">
                                Forward deployed engineering is usually described as the job that
                                sits between the customer and the platform. In practice, for me it
                                has meant sitting across the whole engagement — from the first
                                sales conversation to the production system — and being accountable
                                for the answer staying coherent the entire way.
                            </p>

                            <h4 className="title mt--40">Deciding what to build, and whether to build it</h4>
                            <p className="description">
                                I work with the sales team as the technical voice in the room:
                                understanding the business problem underneath the request,
                                pressure-testing whether it's worth solving, and deciding whether
                                Foundry is genuinely the right platform or whether another
                                technology serves the customer better. That last part matters. A
                                scoping process that only ever returns "yes, proceed" is a sales
                                process wearing a methodology costume. When Foundry <em>is</em> the
                                right answer, I move straight into initial architecture and solution
                                engineering so the shape of the thing is set before anyone writes
                                code.
                            </p>

                            <h4 className="title mt--40">From proof of value to production</h4>
                            <p className="description">
                                I bridge the gap between technical sales and delivery. In proof of
                                value and bootcamp I work directly with the customer and our
                                engineering team to build the first real slice, then stay involved
                                through the engagement so the architecture that ships still answers
                                the business need it was scoped against. As a full-stack FDE my own
                                build work concentrates on AI engineering with AIP and on agentic
                                automation.
                            </p>

                            <h4 className="title mt--40">Building the practice, not just the project</h4>
                            <p className="description">
                                I lead our internal enablement work: a Foundry Center of Excellence,
                                a library of reusable assets, and a Foundry harness that packages
                                our skills, tools, review gates, and conventions into something an
                                engineer installs once. Paired with the Palantir MCP, it means a
                                developer new to the platform starts from our accumulated practice —
                                ontology design gates, migration checks, testing standards,
                                instrumentation defaults — rather than rediscovering them, or
                                repeating a mistake we've already paid for. Every engagement feeds
                                lessons back into the harness, which is the flywheel: delivery
                                improves the practice, and the practice improves delivery.
                            </p>

                            <h4 className="title mt--40">Opportunity to quote, from fourteen days to under three</h4>
                            <p className="description">
                                The engagement I've led to the clearest result is an
                                opportunity-to-quote program for a global solar manufacturer.
                                Quoting ran across disparate systems and long stretches of manual
                                work; we consolidated it into a single ontology-centered Foundry
                                architecture and automated the path through it. Intelligent
                                extraction pulls structured information out of incoming
                                documentation. Explicit decision logic and an identity and
                                permissions model govern who and what can advance each stage.
                                Agentic AI assists the humans in the loop and, in places, does the
                                work outright.
                            </p>
                            <p className="description">
                                Just as important, the whole workflow is instrumented by
                                construction — every stage entered, completed, or blocked is
                                recorded as an event with a server-set timestamp and an actor, so
                                cycle time is a measured fact rather than an estimate. That gives
                                complete data capture and observability across the process:
                                bottlenecks surface quickly, and improvement is continuous rather
                                than anecdotal. Time to quote went from roughly fourteen days to
                                under three.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Row 2: AWS Marketplace */}
            <div className="rn-blog-area pt--80 pb--120 bg_color--5">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section-title text-center">
                                <h2 className="title">AWS Marketplace Deployments</h2>
                                <h4 className="title">6‑week deliveries with architecture + security posture + enablement.</h4>
                            </div>
                        </div>
                    </div>

                    <div className="row mt--30">
                        <div className="col-lg-12">
                            <p className="description text-center" style={{ maxWidth: 900, margin: "0 auto" }}>
                                Click any diagram to open full size.
                            </p>
                        </div>
                    </div>

                    <div className="row mt--40">
                        {marketplaceWork.map((item) => (
                            <div key={item.title} className="col-lg-4 col-md-6 col-12 mt--30">
                                <div className="blog blog-style--1 rn-work-card">
                                    <div className="content">
                                        <h4 className="title">{item.title}</h4>
                                        <div className="blog-text">
                                            <p>{item.body}</p>
                                        </div>
                                        {renderLinks(item.links)}
                                        {item.diagram?.src ? (
                                            <a
                                                href={item.diagram.src}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="d-block mt--20"
                                                aria-label={`${item.title} architecture diagram (open full size)`}
                                            >
                                                <img
                                                    className="w-100"
                                                    src={item.diagram.src}
                                                    alt={item.diagram.alt || `${item.title} architecture diagram`}
                                                    loading="lazy"
                                                    decoding="async"
                                                    style={{
                                                        border: "1px solid rgba(29, 29, 36, 0.12)",
                                                        borderRadius: 10,
                                                        background: "#ffffff",
                                                        maxHeight: 220,
                                                        objectFit: "contain",
                                                    }}
                                                />
                                            </a>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="row mt--60">
                        <div className="col-lg-12 d-flex justify-content-center flex-wrap" style={{ gap: "12px" }}>
                            <Link className="rn-button-style--2 btn-solid" to="/enterprise-ai#marketplace">
                                View Enterprise AI proof panels
                            </Link>
                            <Link className="rn-button-style--2 rn-btn-dark" to="/reference-architectures">
                                Reference architectures (deep dives)
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <FooterTwo />
        </React.Fragment>
    );
};

export default RecentWork;
